export type HospitalCompetitionInput = {
  orgId: string;
  orgName: string;
  learnerCount: number;
  /** Distinct learners who submitted at least one feedback in the period. */
  submitterCount: number;
};

export type HospitalCompetitionRank = {
  orgId: string;
  orgName: string;
  rank: number;
  /** Hospitals tied for the same participation rate share a rank. */
  tied: boolean;
};

/**
 * Short hospital label for charts/lists (drops a leading "BluePearl " brand).
 */
export function formatHospitalLabel(name: string): string {
  return name.replace(/^BluePearl\s+/i, "").trim() || name;
}

function sameParticipation(
  left: { rate: number; submitterCount: number },
  right: { rate: number; submitterCount: number },
): boolean {
  return left.rate === right.rate && left.submitterCount === right.submitterCount;
}

/**
 * Ranks hospitals by share of learners who submitted feedback.
 * When that share matches, the hospital with more people submitting ranks higher.
 * Rates are used only for ordering — callers must not display the %.
 */
export function rankHospitalsByFeedbackParticipation(
  hospitals: HospitalCompetitionInput[],
): HospitalCompetitionRank[] {
  const scored = hospitals
    .filter((hospital) => hospital.learnerCount > 0)
    .map((hospital) => ({
      ...hospital,
      rate: hospital.submitterCount / hospital.learnerCount,
    }))
    .sort((a, b) => {
      if (b.rate !== a.rate) {
        return b.rate - a.rate;
      }
      if (b.submitterCount !== a.submitterCount) {
        return b.submitterCount - a.submitterCount;
      }
      return formatHospitalLabel(a.orgName).localeCompare(
        formatHospitalLabel(b.orgName),
      );
    });

  const ranks: HospitalCompetitionRank[] = [];
  let previous: (typeof scored)[number] | null = null;
  let previousRank = 0;

  scored.forEach((hospital, index) => {
    const rank =
      previous && sameParticipation(hospital, previous)
        ? previousRank
        : index + 1;
    const tied =
      Boolean(previous && sameParticipation(hospital, previous)) ||
      (index + 1 < scored.length &&
        sameParticipation(hospital, scored[index + 1]));

    ranks.push({
      orgId: hospital.orgId,
      orgName: hospital.orgName,
      rank,
      tied,
    });
    previous = hospital;
    previousRank = rank;
  });

  return ranks;
}
